import { GUID } from "@faro-lotv/ielement-types";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Vector3Tuple } from "three";

/** Define how to report the measurement between 2 points. */
export enum ComponentsToDisplay {
  /** display the 3D distance between points */
  single3d = "3D",

  /** display the height and horizontal distance component between points */
  heightAndHorizontal = "heightAndHorizontal",

  /** display the x, y, and z components of the distance between points */
  xyz = "xyz",
}

export type Measurement = {
  /** The unique id of this measurement */
  id: GUID;

  /** The sequence of points for this multipoint measurement */
  points: Vector3Tuple[];

  /** Additional details related to the measurement */
  metadata: {
    /** Name of the measurement */
    name: string;

    /** True, if the measurement is a loop */
    isLoop: boolean;

    /** True, if the measurement points are estimated from a 2D panorama without depth information */
    isEstimatedFrom2DPano?: boolean;

    /** Optional ID of the 360 from where the 3D measurement was taken */
    acquisitionPanoId?: GUID;
  };

  /** The id of the iElement this measurement originated from */
  parentId: GUID;

  /** Which components to display */
  componentsToDisplay: ComponentsToDisplay;
};

/** Represents a wall defined in a panorama, with two floor points and a height */
export type PanoMeasurementWall = {
  /** The unique id of this wall */
  id: GUID;
  /** The id of the panorama this wall belongs to */
  panoId: GUID;
  /** The first point on the floor of the wall*/
  floorPoint1: Vector3Tuple;
  /** The second point on the floor of the wall*/
  floorPoint2: Vector3Tuple;
  /** The height of the wall*/
  height: number;
};

/** Represents tool state during taking measurement in a panorama without depth information */
export enum PanoMeasurementToolState {
  /** No active measurement tool state */
  None = "None",
  /** Start the measurement, waiting to start the first step */
  StartMeasure = "StartMeasure",
  /** Waiting for setting the camera height */
  SetCameraHeight = "SetCameraHeight",
  /** Waiting for select the first point on the floor */
  SelectFloorPoint1 = "SelectFloorPoint1",
  /** Waiting for select the second point on the floor */
  SelectFloorPoint2 = "SelectFloorPoint2",
  /** Waiting for select the third point on the ceiling */
  SelectCeilingPoint = "SelectCeilingPoint",
  /** Wall is ready, waiting for select measurement points*/
  Measuring = "Measuring",
}

/** A data structure to store the set of measurements associated to given element IDs */
export type MeasurementsRecord = Record<GUID, Measurement[]>;

export type MeasurementToolState = {
  /** For each specific element, identified by its GUID, we store the list of measurements associated to it */
  measurements: MeasurementsRecord;

  /** The id of the active (selected) measurement or undefined if none*/
  activeMeasurement?: Measurement;

  /** True if an active measurement is being carried out */
  isMeasurementBeingTaken: boolean;

  /** Number of points placed in the measurement being taken */
  livePointsCount: number;

  /** True while SHIFT locks the next point of the measurement being taken to an axis */
  isAxisLocked: boolean;

  /** Number of measurement taken this session */
  sessionMeasurements: number;

  /** The walls associated to pano measurements */
  panoMeasurementWalls: Record<GUID, PanoMeasurementWall[]>;

  /** The current state of the pano measurement tool */
  panoMeasurementToolState: PanoMeasurementToolState;

  /**
   * The height of the camera above the floor for each pano, used for pano measurements
   * without depth information
   */
  cameraHeight: Record<GUID, number>;

  /**
   * The position of the camera
   */
  cameraPosition: Record<GUID, Vector3Tuple>;

  /**
   * Whether the zoom lens is active.
   * This is a lens that allows the point cloud to be seen while
   * the user is waving the mouse on the pano image.
   */
  isZoomLensActive: boolean;
};

export type OnlyMeasurementToolStore = {
  /** The 'measurementTool' slice of the store */
  measurementTool: MeasurementToolState;
};

export const CAMERA_HEIGHT_DEFAULT = 1.6;
export const CAMERA_HEIGHT_MIN = 0.01;
const CAMERA_HEIGHT_TOLERANCE = 0.001;

const initialState: MeasurementToolState = {
  measurements: {},
  isMeasurementBeingTaken: false,
  livePointsCount: 0,
  isAxisLocked: false,
  sessionMeasurements: 0,
  panoMeasurementWalls: {},
  panoMeasurementToolState: PanoMeasurementToolState.None,
  cameraHeight: {},
  cameraPosition: {},
  isZoomLensActive: false,
};

const measurementToolSlice = createSlice({
  name: "measurementTool",
  initialState,
  reducers: {
    addMeasurement(
      state,
      action: PayloadAction<{ elementID: GUID; measurement: Measurement }>,
    ) {
      state.sessionMeasurements++;
      if (!(action.payload.elementID in state.measurements)) {
        state.measurements[action.payload.elementID] = [];
      }

      state.measurements[action.payload.elementID].push(
        action.payload.measurement,
      );
    },
    removeMeasurement(
      state,
      action: PayloadAction<{ elementID: GUID; measurementID: GUID }>,
    ) {
      const { elementID, measurementID } = action.payload;

      if (!(elementID in state.measurements)) return;
      const measurements = state.measurements[elementID];

      state.measurements[elementID] = measurements.filter(
        (m) => m.id !== measurementID,
      );
    },
    setActiveMeasurement(
      state,
      action: PayloadAction<Measurement | undefined>,
    ) {
      state.activeMeasurement = action.payload;
    },
    setIsMeasurementBeingTaken(state, action: PayloadAction<boolean>) {
      state.isMeasurementBeingTaken = action.payload;
    },
    setLivePointsCount(state, action: PayloadAction<number>) {
      state.livePointsCount = action.payload;
    },
    setIsAxisLocked(state, action: PayloadAction<boolean>) {
      state.isAxisLocked = action.payload;
    },
    setIsMeasurementLoop(
      state,
      action: PayloadAction<{
        elementID: GUID;
        measurementID: GUID;
        isLoop: boolean;
      }>,
    ) {
      const { elementID, measurementID, isLoop } = action.payload;

      if (!(elementID in state.measurements)) return;

      const measurements = state.measurements[elementID];
      const measurement = measurements.find((m) => m.id === measurementID);
      if (!measurement) return;

      measurement.metadata.isLoop = isLoop;
    },
    setMeasurementComponentsToDisplay(
      state,
      action: PayloadAction<{
        elementID: GUID;
        measurementID: GUID;
        componentsToDisplay: ComponentsToDisplay;
      }>,
    ) {
      const { elementID, measurementID, componentsToDisplay } = action.payload;

      if (!(elementID in state.measurements)) return;

      const measurements = state.measurements[elementID];
      const measurement = measurements.find((m) => m.id === measurementID);
      if (!measurement) return;

      measurement.componentsToDisplay = componentsToDisplay;
    },
    addPanoMeasurementWall(
      state,
      action: PayloadAction<{ panoId: GUID; wall: PanoMeasurementWall }>,
    ) {
      if (!(action.payload.panoId in state.panoMeasurementWalls)) {
        state.panoMeasurementWalls[action.payload.panoId] = [];
      }
      state.panoMeasurementWalls[action.payload.panoId].push(
        action.payload.wall,
      );
    },
    removePanoMeasurementWall(state, action: PayloadAction<GUID>) {
      for (const panoId in state.panoMeasurementWalls) {
        const walls = state.panoMeasurementWalls[panoId];
        if (walls.find((wall) => wall.id === action.payload) !== undefined) {
          state.panoMeasurementWalls[panoId] = walls.filter(
            (wall) => wall.id !== action.payload,
          );
          return;
        }
      }
    },
    setPanoMeasurementToolState(
      state,
      action: PayloadAction<PanoMeasurementToolState>,
    ) {
      state.panoMeasurementToolState = action.payload;
    },
    setCameraPosition(
      state,
      action: PayloadAction<{ panoId: GUID; position: Vector3Tuple }>,
    ) {
      state.cameraPosition[action.payload.panoId] = action.payload.position;
    },
    setCameraHeight(
      state,
      action: PayloadAction<{ panoId: GUID; height: number }>,
    ) {
      const newHeight = action.payload.height;
      const { panoId } = action.payload;
      const previousHeight =
        panoId in state.cameraHeight ? state.cameraHeight[panoId] : undefined;

      state.cameraHeight[panoId] = newHeight;
      if (
        previousHeight === undefined ||
        Math.abs(previousHeight - newHeight) < CAMERA_HEIGHT_TOLERANCE
      ) {
        // No change in camera height, do nothing
        return;
      }
      const cameraPosition =
        panoId in state.cameraPosition
          ? state.cameraPosition[panoId]
          : undefined;
      if (cameraPosition === undefined) {
        // Camera position must be defined to be able update pano measurements
        return;
      }
      const heightScale = newHeight / previousHeight;
      const walls = state.panoMeasurementWalls[panoId] ?? [];
      for (const wall of walls) {
        updatePoint(wall.floorPoint1, cameraPosition, heightScale);
        updatePoint(wall.floorPoint2, cameraPosition, heightScale);
        wall.height = wall.height * heightScale;
      }
      const measurements = state.measurements[panoId] ?? [];
      for (const measurement of measurements) {
        for (const point of measurement.points) {
          updatePoint(point, cameraPosition, heightScale);
        }
      }
    },
    setIsZoomLensActive(state, action: PayloadAction<boolean>) {
      state.isZoomLensActive = action.payload;
    },
  },
});

// update point to new camera height by scaling point along the camera ray direction
function updatePoint(
  point: Vector3Tuple,
  cameraPosition: Vector3Tuple,
  heightScale: number,
): void {
  point[0] = cameraPosition[0] + heightScale * (point[0] - cameraPosition[0]);
  point[1] = cameraPosition[1] + heightScale * (point[1] - cameraPosition[1]);
  point[2] = cameraPosition[2] + heightScale * (point[2] - cameraPosition[2]);
}

export const measurementToolReducer = measurementToolSlice.reducer;

export const {
  addMeasurement,
  removeMeasurement,
  setActiveMeasurement,
  setIsMeasurementBeingTaken,
  setLivePointsCount,
  setIsAxisLocked,
  setIsMeasurementLoop,
  setMeasurementComponentsToDisplay,
  addPanoMeasurementWall,
  removePanoMeasurementWall,
  setPanoMeasurementToolState,
  setCameraPosition,
  setCameraHeight,
  setIsZoomLensActive,
} = measurementToolSlice.actions;
